import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useFetch, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Modal } from "@/lib/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays, Printer, Pencil, Send } from "lucide-react";

// Fixed academic range: July 2026 -> July 2027 (13 months inclusive)
const START_YEAR = 2026;
const START_MONTH = 6; // July (0-indexed)
const CAL_MONTHS = Array.from({ length: 13 }, (_, i) => {
  const abs = START_MONTH + i;
  return { year: START_YEAR + Math.floor(abs / 12), month: abs % 12 };
});
const RANGE_START = `${START_YEAR}-07-01`;
const RANGE_END = `${START_YEAR + 1}-07-31`;

const MONTH_LABELS = {
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  fr: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
};
const DOW = { en: ["S", "M", "T", "W", "T", "F", "S"], fr: ["D", "L", "M", "M", "J", "V", "S"] };

// Fixed palette — static class strings so Tailwind keeps them
const COLORS = {
  red: { chip: "bg-red-500 text-white", dot: "bg-red-500", label: { en: "Red", fr: "Rouge" } },
  blue: { chip: "bg-blue-600 text-white", dot: "bg-blue-600", label: { en: "Blue", fr: "Bleu" } },
  green: { chip: "bg-emerald-600 text-white", dot: "bg-emerald-600", label: { en: "Green", fr: "Vert" } },
  orange: { chip: "bg-orange-500 text-white", dot: "bg-orange-500", label: { en: "Orange", fr: "Orange" } },
  purple: { chip: "bg-purple-600 text-white", dot: "bg-purple-600", label: { en: "Purple", fr: "Violet" } },
  gray: { chip: "bg-gray-600 text-white", dot: "bg-gray-600", label: { en: "Gray", fr: "Gris" } },
};
const COLOR_KEYS = Object.keys(COLORS);

const TABS = [
  { key: "other", en: "Other Sections", fr: "Autres sections" },
  { key: "chez_kids", en: "Chez Kids", fr: "Chez Kids" },
];

function pad(n) { return String(n).padStart(2, "0"); }

export default function AnnualCalendar({ canEdit = false }) {
  const { L, lang } = useLang();
  const { data: events, reload } = useFetch(() => api.get("/events"));
  const [tab, setTab] = useState("other");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState({ title: "", description: "", date: "", color: "blue", section: "other" });

  const sectionEvents = (events || []).filter((e) => (e.section || "other") === tab);
  const byDate = {};
  sectionEvents.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });

  const openAdd = (dateStr) => { setEditId(null); setF({ title: "", description: "", date: dateStr, color: "blue", section: tab }); setOpen(true); };
  const openEdit = (e) => { setEditId(e.id); setF({ title: e.title || "", description: e.description || "", date: e.date, color: e.color || "blue", section: e.section || "other" }); setOpen(true); };
  const save = async () => {
    if (saving) return;
    if (!f.title.trim() || !f.date) { toast.error(L("Title and date are required", "Titre et date requis")); return; }
    setSaving(true);
    const payload = { title: f.title.trim(), description: f.description || null, date: f.date, type: "program", color: f.color, section: f.section };
    try {
      if (editId) { await api.patch(`/events/${editId}`, payload); toast.success(L("Entry updated", "Entrée mise à jour")); }
      else { await api.post("/events", payload); toast.success(L("Added to calendar", "Ajouté au calendrier")); }
      setOpen(false); setEditId(null); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (e) => { if (!window.confirm(L(`Are you sure you want to delete ${e.title}?`, `Êtes-vous sûr de vouloir supprimer ${e.title} ?`))) return; try { await api.del(`/events/${e.id}`); reload(); } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } };
  const notifyEvent = async (e) => {
    if (!window.confirm(L(`Send an email about "${e.title}" to all parents now?`, `Envoyer un email à propos de « ${e.title} » à tous les parents maintenant ?`))) return;
    try { const r = await api.post(`/events/${e.id}/notify`, {}); toast.success(L(`Notifications sent to ${r.emailed} parents`, `Notifications envoyées à ${r.emailed} parents`)); reload(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const monthLabels = MONTH_LABELS[lang === "fr" ? "fr" : "en"];
  const dow = DOW[lang === "fr" ? "fr" : "en"];
  const colorOf = (e) => COLORS[e.color] || COLORS.blue;

  const renderMonth = ({ year, month }) => {
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDow = first.getDay();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return (
      <div key={`${year}-${month}`} className="border border-border rounded-lg p-3 break-inside-avoid bg-card" data-testid={`cal-month-${year}-${month}`}>
        <p className="font-heading font-bold text-sm mb-2">{monthLabels[month]} {year}</p>
        <div className="grid grid-cols-7 gap-1 text-center mb-1">
          {dow.map((d, i) => <div key={i} className="text-[10px] font-medium text-muted-foreground">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[64px]" />;
            const ds = `${year}-${pad(month + 1)}-${pad(d)}`;
            const evs = byDate[ds] || [];
            return (
              <div key={i} data-testid={`cal-day-${ds}`}
                className={`relative min-h-[64px] rounded border border-border/60 p-1 text-left bg-white ${canEdit ? "hover:ring-1 hover:ring-primary cursor-pointer" : ""}`}
                onClick={() => canEdit ? openAdd(ds) : null}>
                <span className="text-[11px] font-semibold text-foreground">{d}</span>
                <div className="space-y-0.5 mt-0.5">
                  {evs.map((e) => (
                    <div key={e.id} onClick={(ev) => { ev.stopPropagation(); if (canEdit) openEdit(e); }}
                      className={`text-[9px] leading-tight rounded px-1 py-0.5 truncate ${colorOf(e).chip} ${canEdit ? "cursor-pointer hover:opacity-80" : ""}`}
                      title={canEdit ? L("Click to edit or delete", "Cliquer pour modifier ou supprimer") : e.title} data-testid={`cal-chip-${e.id}`}>
                      {e.title}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const upcoming = [...sectionEvents].sort((a, b) => a.date.localeCompare(b.date));
  const tabLabel = (t) => (lang === "fr" ? t.fr : t.en);

  return (
    <div className="animate-fade-up" id="calendar-print">
      <div className="flex items-center justify-between mb-4 no-print">
        <div>
          <h2 className="font-heading font-extrabold text-2xl flex items-center gap-2"><CalendarDays className="h-6 w-6 text-primary" />{L("School Calendar", "Calendrier scolaire")} {START_YEAR}–{START_YEAR + 1}</h2>
          <p className="text-muted-foreground text-sm">{canEdit ? L("Click a date to add an activity. Each activity has its own color.", "Cliquez sur une date pour ajouter une activité. Chaque activité a sa propre couleur.") : L("The school's annual program (July to July).", "Le programme annuel de l'école (juillet à juillet).")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="cal-print-btn"><Printer className="h-4 w-4 mr-1" />{L("Print", "Imprimer")}</Button>
          {canEdit && <Button onClick={() => openAdd(`${START_YEAR}-07-01`)} data-testid="cal-add-btn"><Plus className="h-4 w-4 mr-1" />{L("Add activity", "Ajouter une activité")}</Button>}
        </div>
      </div>

      {/* Calendar tabs */}
      <div className="flex gap-2 mb-4 no-print" data-testid="cal-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`cal-tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === t.key
              ? (t.key === "chez_kids" ? "bg-emerald-600 text-white" : "bg-primary text-primary-foreground")
              : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {tabLabel(t)}
          </button>
        ))}
      </div>

      <div className="hidden print:block mb-4">
        <h2 className="font-bold text-xl text-center">Institution Chrétienne D'Haïti — {L("School Calendar", "Calendrier scolaire")} {START_YEAR}–{START_YEAR + 1} · {tabLabel(TABS.find((x) => x.key === tab))}</h2>
      </div>

      {/* Color legend */}
      <div className="flex flex-wrap gap-3 mb-4 bg-secondary/50 border border-border rounded-lg p-3" data-testid="cal-legend">
        <span className="text-xs font-semibold text-muted-foreground self-center">{L("Legend", "Légende")}:</span>
        {COLOR_KEYS.map((c) => (
          <span key={c} className="flex items-center gap-1.5 text-xs">
            <span className={`h-3 w-3 rounded ${COLORS[c].dot}`} />{COLORS[c].label[lang === "fr" ? "fr" : "en"]}
          </span>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`cal-grid-${tab}`}>
        {CAL_MONTHS.map(renderMonth)}
      </div>

      <div className="mt-6 no-print">
        <p className="font-heading font-bold mb-2">{L("All activities", "Toutes les activités")} — {tabLabel(TABS.find((x) => x.key === tab))}</p>
        {upcoming.length === 0 ? <Empty title={L("No activities yet", "Aucune activité")} /> : (
          <div className="space-y-2" data-testid="cal-entries">
            {upcoming.map((e) => (
              <div key={e.id} className="flex items-start gap-3 bg-card border border-border rounded-lg p-3" data-testid={`cal-entry-${e.id}`}>
                <span className={`h-3 w-3 rounded mt-1.5 shrink-0 ${colorOf(e).dot}`} />
                <div className="text-center shrink-0 w-14">
                  <p className="text-primary font-bold">{e.date.slice(8, 10)}</p>
                  <p className="text-xs text-muted-foreground">{monthLabels[parseInt(e.date.slice(5, 7)) - 1]?.slice(0, 3)} {e.date.slice(0, 4)}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{e.title}</p>
                  {e.description && <p className="text-sm text-muted-foreground">{e.description}</p>}
                </div>
                {canEdit && <div className="flex gap-2 shrink-0 items-center">
                  {e.notified_at && <span className="text-[10px] text-emerald-600 font-medium" data-testid={`cal-notified-${e.id}`}>{L("Notified ✓", "Notifié ✓")}</span>}
                  <Button size="sm" variant="outline" onClick={() => notifyEvent(e)} data-testid={`cal-notify-${e.id}`}><Send className="h-4 w-4 mr-1" />{L("Send Notifications", "Envoyer notifications")}</Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(e)} data-testid={`cal-edit-${e.id}`}><Pencil className="h-4 w-4 mr-1" />{L("Edit", "Modifier")}</Button>
                  <Button size="sm" variant="outline" onClick={() => del(e)} data-testid={`cal-del-${e.id}`} className="text-destructive border-destructive/40 hover:bg-destructive/10"><Trash2 className="h-4 w-4 mr-1" />{L("Delete", "Supprimer")}</Button>
                </div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? L("Edit activity", "Modifier l'activité") : L("Add activity", "Ajouter une activité")}
        footer={<div className="flex gap-2 justify-between w-full">
          {editId ? <Button variant="outline" onClick={() => { del({ id: editId, title: f.title }); setOpen(false); setEditId(null); }} data-testid="cal-modal-delete-btn" className="text-destructive border-destructive/40 hover:bg-destructive/10"><Trash2 className="h-4 w-4 mr-1" />{L("Delete", "Supprimer")}</Button> : <span />}
          <Button onClick={save} disabled={saving} data-testid="cal-save-btn">{saving ? L("Saving…", "Enregistrement…") : L("Save", "Enregistrer")}</Button>
        </div>}>
        <Field label={L("Calendar", "Calendrier")}>
          <Select value={f.section} onValueChange={(v) => setF({ ...f, section: v })}>
            <SelectTrigger data-testid="cal-section-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="other">{L("Other Sections", "Autres sections")}</SelectItem>
              <SelectItem value="chez_kids">{L("Chez Kids", "Chez Kids")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={L("Date", "Date")}><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="cal-date-input" /></Field>
        <Field label={L("Title", "Titre")}><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={L("e.g. First term exams", "ex. Examens du 1er trimestre")} data-testid="cal-title-input" /></Field>
        <Field label={L("Color", "Couleur")}>
          <div className="flex flex-wrap gap-2" data-testid="cal-color-picker">
            {COLOR_KEYS.map((c) => (
              <button key={c} type="button" onClick={() => setF({ ...f, color: c })}
                className={`h-8 w-8 rounded-full ${COLORS[c].dot} ${f.color === c ? "ring-2 ring-offset-2 ring-primary" : ""}`}
                title={COLORS[c].label[lang === "fr" ? "fr" : "en"]} data-testid={`cal-color-${c}`} />
            ))}
          </div>
        </Field>
        <Field label={L("Description (optional)", "Description (optionnel)")}><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="cal-desc-input" /></Field>
        <p className="text-xs text-muted-foreground" data-testid="cal-local-only-note">{L("Saved to the calendar only. Use 'Send Notifications' on the activity to email parents when you're ready.", "Enregistré dans le calendrier uniquement. Utilisez « Envoyer notifications » sur l'activité pour prévenir les parents le moment venu.")}</p>
      </Modal>
    </div>
  );
}
